import React from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Button from 'core/elements/button'
import { BatchActionButtonProps } from 'core/elements/grid/hooks/useGridSelectableRows'
import { ButtonProps } from 'core/elements/button/Button'
import Tooltip from 'core/elements/tooltip'
import { topMiddle } from 'core/elements/menu/defaults'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { noop } from 'utils/fp'

export type GridDefaultActionButtonProps<T> = ButtonProps & BatchActionButtonProps<T>

export default function GridDefaultActionButton<T>({
  className,
  disabled,
  children,
  onClick,
  icon,
  iconOnly,
  solidIcon = true,
  tooltip,
  ...rest
}: GridDefaultActionButtonProps<T>) {
  const classes = useStyles({ disabled })
  const button = iconOnly ? (
    <div className={classes.iconContainer}>
      <FontAwesomeIcon onClick={disabled ? noop : onClick} className={classes.icon}>
        {icon}
      </FontAwesomeIcon>
    </div>
  ) : (
    <Button
      {...rest}
      className={clsx(classes.btn, className)}
      icon={icon}
      variant="secondary"
      disabled={disabled}
      onClick={onClick}
      solidIcon={solidIcon}
    >
      {children}
    </Button>
  )
  if (tooltip) {
    return (
      <Tooltip message={tooltip} align={topMiddle.align} offset={topMiddle.offset}>
        {button}
      </Tooltip>
    )
  }
  return button
}

const useStyles = makeStyles<Theme, { disabled: boolean }>((theme) => ({
  btn: {
    opacity: ({ disabled = false }) => (disabled ? 0.5 : 1),
    '& > .button-text > .button-icon': {
      fontWeight: 'normal',
      fontSize: 16,
    },
    '& > .button-text': {
      fontWeight: 'normal',
    },
    '&:hover > .button-text': {
      fontWeight: 'normal',
    },
  },
  iconContainer: {
    padding: '6px 4px',
    borderRadius: 4,
    '&:hover': {
      background: theme.components.checkbox.hoverBackground,
    },
  },
  icon: {
    cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
    fontSize: 16,
    padding: '0px 8px',
    color: ({ disabled }) => (disabled ? theme.components.iconButton.disabledColor : 'inherit'),
  },
}))
